import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Select, Input, Button, Card, Tooltip, Divider, Modal, Switch } from "antd";
import {
  Settings,
  Settings2,
  FileText,
  Info
} from "lucide-react";
import { getInstruments, getExpiries, getOptionChainInstrumentsData, getPrice } from "../../../../redux/actions/optionChain";
import { createStrategy as apiCreateStrategy, updateStrategy as apiUpdateStrategy, getStrategy, getStrategies } from "../../../../api/strategyApi";
import { getSpotToken } from "../../../../api/dataApi";
import PlusMinQty from "../../../utilities/PlusMinQty";
import { buildTemplateLegs } from "../../../../helpers/strategyTemplates";
import "../QuiktradeAlgo.css";
import { getBrokers } from "../../../../api/authentication";
import LinkBrokers from "./LinkBrokers";
import SubscriptionCheckModal from "components/common/SubscriptionCheckModal";
import { strategyTemplates } from "../utils";
import StrategyLegs from "./StrategyLegs";
import { useToast } from "components/common/custom-toast/Toast";
import StrategySavedModal from "./StrategySavedModal";
import { helpText } from "../../../../util/helpText";

const { Option, OptGroup } = Select;
const { confirm } = Modal;

const defExecutionMode = ['parallel', 'sequential'];

const INITIAL_FORM_DATA = {
  brokers: [],
  strategyName: "",
  description: "",
  symbol: "",
  entryExitMode: "signal_based",
  intradayPositional: "intraday",
  entryTime: "09:16",
  exitTime: "15:25",
  daysToRun: [1, 2, 3, 4, 5],
  conditionWait: "",
  autoRollOverTime: "15:29",
  atmSelection: "Spot",
  strategyTemplate: null,
  entryOrderType: "Mkt protect",
  entryBufferPct: "5",
  exitOrderType: "Mkt protect",
  exitBufferPct: "5",
  product: "NRML",
  variety: "REGULAR",
  validity: "DAY",
  maxLotsPerOrder: 1,
  limitOrderTimeoutAction: "Convert to Market",
  limitOrderTimeoutDuration: 5,
  enableReEntryAfterRms: false,
  entryExecutionMode: "sequential",
  entryBuySellDelaySeconds: 3,
  exitExecutionMode: "sequential",
  exitBuySellDelaySeconds: 3,
  allOrNothingEntry: false,
  allOrNothingExit: false,
  rms: {
    basis: 0,
    stopLossInput: {
      valueType: 0,
      value: ""
    },
    takeProfitInput: {
      valueType: 0,
      value: ""
    },
    trailSL: {
      valueType: 2,
      reference: "",
      step: ""
    },
    profitLockAt: "",
    profitLockSl: "",
    profitLockTrail: {
      valueType: 2,
      reference: "",
      step: ""
    },
    perLot: false
  }
};

const INITIAL_TRADING_LEGS = [
  {
    id: 1,
    idle: false,
    side: "SELL",
    futOpt: "OPT",
    optionType: "CE",
    strikeSelection: "Distance",
    strikeValue: "0",
    expirySelection: "Relative",
    expiry: "Current Monthly",
    baseQty: 1,
    qty: 1,
    oldQty: 1,
    onSlAction: 0,
    onSlReEntryCount: 0,
    onSlTargetStrategyId: "",
    rms: {
      basis: 0,
      stopLossInput: {
        valueType: 0,
        value: ""
      },
      takeProfitInput: {
        valueType: 0,
        value: ""
      },
      trailSL: {
        valueType: 2,
        reference: "",
        step: ""
      },
      profitLockAt: "",
      profitLockSl: "",
      profitLockTrail: {
        valueType: 2,
        reference: "",
        step: ""
      }
    },
    onTgtAction: "Exit Leg"
  },
  {
    id: 2,
    idle: false,
    side: "SELL",
    futOpt: "OPT",
    optionType: "PE",
    strikeSelection: "Distance",
    strikeValue: "0",
    expirySelection: "Relative",
    expiry: "Current Monthly",
    baseQty: 1,
    qty: 1,
    oldQty: 1,
    onSlAction: 0,
    onSlReEntryCount: 0,
    onSlTargetStrategyId: "",
    rms: {
      basis: 0,
      stopLossInput: {
        valueType: 0,
        value: ""
      },
      takeProfitInput: {
        valueType: 0,
        value: ""
      },
      trailSL: {
        valueType: 2,
        reference: "",
        step: ""
      },
      profitLockAt: "",
      profitLockSl: "",
      profitLockTrail: {
        valueType: 2,
        reference: "",
        step: ""
      }
    },
    onTgtAction: "Exit Leg"
  }
];

const QuiktradeAlgo = () => {
  const toast = useToast()
  const { strategyId } = useParams();
  const isEditMode = Boolean(strategyId);
  const qtyVal = useRef(1);
  const [multiplier, setMultiplier] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [instrumentOptions, setInstrumentOptions] = useState([]);
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(false);
  const [absoluteExpiryOptions, setAbsoluteExpiryOptions] = useState([]);
  const [tradingLegs, setTradingLegs] = useState(INITIAL_TRADING_LEGS);
  const [isFormValid, setIsFormValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedStrategyId, setSavedStrategyId] = useState("");
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isSavedModalOpen, setIsSavedModalOpen] = useState(false);
  const [legErrorMessages, setLegErrorMessages] = useState({});
  const [riskSettingsErrorMessages, setRiskSettingsErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [strikeListCache, setStrikeListCache] = useState({});
  const info = useSelector((state) => state?.info);
  const [loadStrategyId, setLoadStrategyId] = useState("");
  const [strategyList, setStrategyList] = useState([]);
  const [sellFirst, setSellFirst] = useState(false);
  const [atmStrike, setAtmStrike] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [optChainData, setOptChainData] = useState(null)
  const accessiblePlans = useSelector((state) => state?.packages?.accessiblePlans);
  const plansLoading = accessiblePlans === undefined;
  const hasAlgoAccess = accessiblePlans?.length > 0 ? accessiblePlans?.some(planName =>
    planName === 'QT-Unlimited' || planName === 'QT-Algo' || planName === 'trial'
  ) : false;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state?.user?.isLoggedIn === true);
  const brokerLoggedIn = useSelector((state) => state?.user?.brokerLoggedIn === true);
  const user = useSelector((state) => state?.user);

  const [brokers, setBrokers] = useState([]);
  const brokerOptions = useSelector((s) => s?.brokers?.list || []);
  const brokersList = useSelector((state) => state?.brokers?.list || []);
  const getBrokerLabel = useCallback((id) => {
    const b = (brokerOptions || []).find(x => (x?.id?.toString?.() || '') === (id?.toString?.() || ''));
    if (!b) return id;
    return b?.name?.toUpperCase() || `${b?.type?.toUpperCase?.()} - ${b?.userName?.toUpperCase?.()}` || id;
  }, [brokerOptions]);

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => {
    return "₹₹";
  };
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");
  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };
  const onClickSlType = () => {
    const basisValue = formData?.rms?.basis === 1 ? "Comb Prem" : "PNL";
    if (basisValue === "PNL") {
      const newType = cycleWithin(formData.rms.stopLossInput.valueType, [0, 2]);
      handleInputChange('rms.stopLossInput.valueType', newType);
      isValidRiskSettingsSLTP("sl", formData?.rms?.stopLossInput?.value, formData?.rms?.basis);
      return;
    }
    const currentType = formData.rms.stopLossInput.valueType;
    const newType = cycleWithin(currentType, [1, 2]);
    handleInputChange('rms.stopLossInput.valueType', newType);
    isValidRiskSettingsSLTP("sl", formData?.rms?.stopLossInput?.value, formData?.rms?.basis);
  };
  const onClickTpType = () => {
    const basisValue = formData?.rms?.basis === 1 ? "Comb Prem" : "PNL";
    if (basisValue === "PNL") {
      const newType = cycleWithin(formData.rms.takeProfitInput.valueType, [0, 2]);
      handleInputChange('rms.takeProfitInput.valueType', newType);
      isValidRiskSettingsSLTP("tp", formData?.rms?.takeProfitInput?.value, formData?.rms?.basis);
      return;
    }
    const currentType = formData.rms.takeProfitInput.valueType;
    const newType = cycleWithin(currentType, [1, 2]);
    handleInputChange('rms.takeProfitInput.valueType', newType);
    isValidRiskSettingsSLTP("tp", formData?.rms?.takeProfitInput?.value, formData?.rms?.basis);
  };
  const onClickTrailType = () => {
    const basisValue = formData?.rms?.basis === 1 ? "Comb Prem" : "PNL";
    if (basisValue === "PNL") {
      handleInputChange('rms.trailSL.valueType', 2);
      return;
    }
    const currentType = formData.rms.trailSL.valueType;
    const newType = cycleWithin(currentType, [2, 1]);
    handleInputChange('rms.trailSL.valueType', newType);
  };


  const setLegMsg = (legId, type, ok, customMsg = null) => {
    setLegErrorMessages(prev => {
      const legErrors = prev[legId] || { stopLoss: "", target: "", qty: "" };
      if (type === "sl") {
        legErrors.stopLoss = ok ? "" : (customMsg || "");
      } else if (type === "tp") {
        legErrors.target = ok ? "" : (customMsg || "");
      } else if (type === "qty") {
        legErrors.qty = ok ? "" : (customMsg || "");
      }
      return { ...prev, [legId]: legErrors };
    });
  };

  const isValidLegQty = (legId, val) => {
    const parsedVal = parseFloat(val);
    if (!val || isNaN(parsedVal) || parsedVal <= 0) {
      console.log("trgr", legId, val)
      setLegMsg(legId, "qty", false, "Lots must be greater than 0");
      return false;
    }
    setLegMsg(legId, "qty", true);
    return true;
  };


  const isValidLegSLTP = (legId, type, val, leg) => {
    if (val === null || val === undefined || val === '' || val === 0) {
      setLegMsg(legId, type, true);
      return true;
    }
    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setLegMsg(legId, type, true);
      return true;
    }

    const basisIsPNL = (leg?.rms?.basis ?? 0) === 0;

    if (!basisIsPNL) {
      if (parsedVal <= 0) {
        setLegMsg(legId, type, false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        return false;
      }
    } else {
      const slValueType = leg.rms.stopLossInput.valueType;
      const tpValueType = leg.rms.takeProfitInput.valueType;
      if (type === "sl" && parsedVal < 0 && slValueType == 2) {
        setLegMsg(legId, type, false, "Stop Loss cannot be negative");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && tpValueType == 2) {
        setLegMsg(legId, type, false, "Target cannot be negative");
        return false;
      }
      if (type === "sl" && parsedVal > 0 && slValueType !== 2) {
        setLegMsg(legId, type, false, "Stop Loss cannot be positive");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && tpValueType !== 2) {
        setLegMsg(legId, type, false, "Target cannot be negative");
        return false;
      }
    }

    setLegMsg(legId, type, true);
    return true;
  };

  // Validation functions for risk settings (similar to StrategyRiskModal)
  const setRiskSettingsMsg = (type, ok, customMsg = null) => {
    if (type === "sl") {
      setRiskSettingsErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : (customMsg || "") }));
    } else {
      setRiskSettingsErrorMessages((prev) => ({ ...prev, target: ok ? "" : (customMsg || "") }));
    }
  };


  const isValidRiskSettingsSLTP = (type, val, basis) => {
    if (val === null || val === undefined || val === '' || val === 0) {
      setRiskSettingsMsg(type, true);
      return true;
    }
    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setRiskSettingsMsg(type, true);
      return true;
    }

    const basisIsPNL = (basis ?? 0) === 0;

    if (basisIsPNL) {
      const slValueType = formData.rms.stopLossInput.valueType;
      const tpValueType = formData.rms.takeProfitInput.valueType;
      if (type === "sl" && parsedVal < 0 && slValueType == 2) {
        setRiskSettingsMsg(type, false, "Stop Loss cannot be negative");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && tpValueType == 2) {
        setRiskSettingsMsg(type, false, "Target cannot be negative");
        return false;
      }
    }

    setRiskSettingsMsg(type, true);
    return true;
  };

  const validateRiskSettingsTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setRiskSettingsErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setRiskSettingsErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setRiskSettingsErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateRiskSettingsProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setRiskSettingsErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setRiskSettingsErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateRiskSettingsProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setRiskSettingsErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setRiskSettingsErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setRiskSettingsErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  const handleSellFirstToggle = (checked) => {
    setSellFirst(checked);
    if (checked) {
      const sortedLegs = [...tradingLegs].sort((a, b) => {
        if (a.side === 'SELL' && b.side === 'BUY') return -1;
        if (a.side === 'BUY' && b.side === 'SELL') return 1;
        return 0;
      });
      setTradingLegs(sortedLegs);
    }
  };

  useEffect(() => {
    if (sellFirst) {
      const sortedLegs = [...tradingLegs].sort((a, b) => {
        if (a.side === 'SELL' && b.side === 'BUY') return -1;
        if (a.side === 'BUY' && b.side === 'SELL') return 1;
        return 0;
      });

      const orderChanged = sortedLegs.some((leg, index) => leg.id !== tradingLegs[index].id);
      if (orderChanged) {
        setTradingLegs(sortedLegs);
      }
    }
  }, [tradingLegs.length, tradingLegs.map(leg => leg.side).join(','), sellFirst]);

  useEffect(() => {
    if (isLoggedIn && brokerLoggedIn) {
      fetchInstruments();
      (async () => {
        try {
          const br = await getBrokers("loggedIn");
          const list = br?.data?.brokers?.filter((b) => b?.quickTrade && (b?.purpose === 'algo' || b?.type === 'papertrading')) || [];
          setBrokers(list);
        } catch (e) {
          setBrokers([]);
        }
      })();
    }
  }, [isLoggedIn, brokerLoggedIn]);

  const fetchInstruments = useCallback(async () => {
    try {
      setIsLoadingInstruments(true);
      const data = await dispatch(getInstruments());

      if (data && data?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate("/authentication/login");
        return;
      }

      if (data && Array.isArray(data)) {
        const instruments = data.map((instrument) => instrument.tradingName);
        setInstrumentOptions(instruments);

        const defaultSymbol = instruments.includes('NIFTY') ? 'NIFTY' : instruments[0];
        if (!formData.symbol && instruments.length > 0 && !isEditMode) {
          setFormData(prev => ({
            ...prev,
            symbol: defaultSymbol,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching instruments:", error);
      toast.error(error || "Failed to fetch instruments");
    } finally {
      setIsLoadingInstruments(false);
    }
  }, [dispatch, navigate, formData.symbol]);

  const fetchStrikeList = useCallback(async (symbol) => {
    if (!symbol) return [];
    try {
      let firstExpiry = Array.isArray(absoluteExpiryOptions) && absoluteExpiryOptions.length > 0
        ? absoluteExpiryOptions[0]
        : '';
      if (!firstExpiry) {
        const exps = await dispatch(getExpiries(symbol));
        if (Array.isArray(exps) && exps.length > 0) {
          firstExpiry = exps[0];
        }
      }
      if (!firstExpiry) return [];
      const res = await dispatch(getOptionChainInstrumentsData(symbol, firstExpiry));
      const strikes = Array.isArray(res?.optData)
        ? Array.from(new Set(res.optData.map((x) => x?.strikePrice))).sort((a, b) => a - b)
        : [];
      setStrikeListCache((prev) => ({ ...prev, [symbol]: strikes }));
      const atmStrike = await getATMStrike(symbol, strikes);
      setAtmStrike(atmStrike);
      setFormData(prev => ({
        ...prev,
        maxLotsPerOrder: !res.volumeFreeze ? 50 : Math.floor(res.volumeFreeze / res.lotSize)
      }));
      setOptChainData(res)
      return strikes;
    } catch (e) {
      return [];
    }
  }, [absoluteExpiryOptions]);

  useEffect(() => {
    if (formData.symbol) {
      fetchStrikeList(formData.symbol);
    }
  }, [absoluteExpiryOptions]);


  const getATMStrike = useCallback(async (symbol, strikes) => {
    try {
      const data = await getPrice(
        symbol,
        symbol == "SENSEX" || symbol == "BANKEX"
          ? "BSE"
          : "NSE",
        ""
      );
      if (data?.lp) {
        const spotPrice = data.lp;
        if (!strikes.length) return null;

        let atmStrike = strikes[0];
        let minDiff = Math.abs(strikes[0] - spotPrice);

        for (const strike of strikes) {
          const diff = Math.abs(strike - spotPrice);
          if (diff < minDiff) {
            minDiff = diff;
            atmStrike = strike;
          }
        }

        return atmStrike;
      }
    } catch (e) {
      console.error("Error getting ATM strike:", e);
    }
    return null;
  }, [strikeListCache]);

  // useEffect(() => {
  //   const fetchATMStrike = async () => {
  //     if (!formData.symbol) return;
  //     try {
  //       const atmStrike = await getATMStrike(formData.symbol);
  //       console.log("atmStrike", atmStrike);
  //       if (atmStrike) {
  //         setAtmStrike(atmStrike);
  //       }
  //     } catch (e) {
  //       console.error("Error getting ATM strike:", e);
  //     }
  //   };
  //   fetchATMStrike();
  // }, [absoluteExpiryOptions]);

  useEffect(() => {
    const fetchExpiriesForSymbol = async () => {
      if (!formData.symbol) return;
      try {
        const exps = await dispatch(getExpiries(formData.symbol));
        if (exps && !exps?.sessionExpired) {
          setAbsoluteExpiryOptions(Array.isArray(exps) ? exps : []);
        }
      } catch (e) {
        setAbsoluteExpiryOptions([]);
      }
    };
    fetchExpiriesForSymbol();
  }, [formData.symbol]);


  const isBrokersLoading = useSelector((state) => state?.brokers?.isLoading);
  const isPlansLoading = useSelector((state) => state?.packages?.isLoading);

  useEffect(() => {
    if (isBrokersLoading || isPlansLoading) {
      return;
    }

    if (!isLoggedIn) {
      toast.error("Please login to access Algo signals");
      navigate("/authentication/login");
      return;
    }

    if (hasAlgoAccess === false) {
      setShowSubscriptionModal(true);
      return;
    }

    if (!brokerLoggedIn) {
      toast.error("Please connect your broker account to access Algo signals");
      navigate("/broker-login");
      return;
    }

    const purposed = (brokersList || []).filter(b => (b.purpose === 'algo' || b.type === 'papertrading'));
    if (purposed.length === 0) {
      toast.error("Please use broker account with purpose 'algo' to access Algo signals");
      navigate("/broker-login");
      return;
    }

  }, [isLoggedIn, brokerLoggedIn, hasAlgoAccess, isPlansLoading, isBrokersLoading]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const next = { ...prev };
      if (field === 'basis') {
        next.rms = { ...next.rms, basis: value };
        if (value === 0) { // PNL
          next.rms.stopLossInput.valueType = 0;
          next.rms.takeProfitInput.valueType = 0;
          next.rms.trailSL.valueType = 2;
          next.rms.perLot = false;
        } else {
          next.rms.stopLossInput.valueType = 2;
          next.rms.takeProfitInput.valueType = 2;
          next.rms.trailSL.valueType = 2;
        }
        setRiskSettingsErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
      } else if (field.startsWith('rms.')) {
        const rmsField = field.split('.')[1];
        if (rmsField === 'stopLossInput' || rmsField === 'takeProfitInput' || rmsField === 'trailSL' || rmsField === 'profitLockTrail') {
          const subField = field.split('.')[2];
          next.rms[rmsField] = { ...next.rms[rmsField], [subField]: value };

          if (rmsField === 'stopLossInput' && subField === 'value') {
            if (!value || value === 0 || value === null) {
              next.rms.trailSL = { ...next.rms.trailSL, reference: "", step: "" };
            }
            setTimeout(() => {
              isValidRiskSettingsSLTP("sl", value, next.rms.basis);
            }, 0);
          } else if (rmsField === 'takeProfitInput' && subField === 'value') {
            setTimeout(() => {
              isValidRiskSettingsSLTP("tp", value, next.rms.basis);
            }, 0);
          } else if (rmsField === 'trailSL') {
            if (subField === 'reference' || subField === 'step') {
              setTimeout(() => {
                validateRiskSettingsTrailSL(
                  subField === 'reference' ? value : next.rms.trailSL.reference,
                  subField === 'step' ? value : next.rms.trailSL.step
                );
              }, 0);
            }
          } else if (rmsField === 'profitLockTrail') {
            if (subField === 'reference' || subField === 'step') {
              setTimeout(() => {
                validateRiskSettingsProfitLockTrail(
                  subField === 'reference' ? value : next.rms.profitLockTrail.reference,
                  subField === 'step' ? value : next.rms.profitLockTrail.step
                );
              }, 0);
            }
          }

          if ((rmsField === 'profitLockAt' || rmsField === 'profitLockSl') && (!value || value === 0 || value === null)) {
            next.rms.profitLockTrail = { ...next.rms.profitLockTrail, reference: "", step: "" };
          }
        } else {
          next.rms[rmsField] = value;

          if (rmsField === 'profitLockAt' || rmsField === 'profitLockSl') {
            setTimeout(() => {
              validateRiskSettingsProfitLock(
                rmsField === 'profitLockAt' ? value : next.rms.profitLockAt,
                rmsField === 'profitLockSl' ? value : next.rms.profitLockSl
              );
            }, 0);
            if (!value || value === 0 || value === null) {
              next.rms.profitLockTrail = { ...next.rms.profitLockTrail, reference: "", step: "" };
            }
          }
        }
      } else {
        if (field === 'entryExitMode') {
          if (value === 'time_based') {
            toast.info("Please link your broker(s) below for time-based strategy execution.");
            if (prev.intradayPositional === 'positional') {
              next.intradayPositional = 'intraday';
              toast.info("Time based strategies must be Intraday. Changed to Intraday.");
            }
          }
        }
        if (field === 'intradayPositional' && value === 'positional') {
          if (prev.entryExitMode === 'time_based') {
            toast.error("Positional strategies cannot be Time based. Please change to Signal based first.");
            return prev;
          }
          if (prev.product === 'MIS') {
            toast.error("Positional strategies cannot have MIS product type. Please change product type to NRML first.");
            return prev;
          }
        }
        if (field === 'product' && value === 'MIS' && prev.intradayPositional === 'positional') {
          toast.error("MIS product type is not allowed for Positional strategies. Please change to Intraday first.");
          return prev;
        }
        if (field === 'entryExecutionMode' && value === 'parallel') {
          next.entryBuySellDelaySeconds = null;
        }
        if (field === 'exitExecutionMode' && value === 'parallel') {
          next.exitBuySellDelaySeconds = null;
        }
        next[field] = value;
      }
      return next;
    });
    if (field === 'symbol') {

      const weeklySupported = value === 'NIFTY' || value === 'SENSEX';
      if (!weeklySupported) {
        setTradingLegs(prev => prev.map(leg => (
          leg.expiry === 'Current Weekly' || leg.expiry === 'Next Weekly'
            ? { ...leg, expiry: 'Current Monthly' }
            : leg
        )));
      }
      (async () => {
        try {
          const exps = await dispatch(getExpiries(value));
          if (exps && !exps.sessionExpired) {
            setAbsoluteExpiryOptions(Array.isArray(exps) ? exps : []);
          }
        } catch (e) {
          setAbsoluteExpiryOptions([]);
        }
      })();
    }
  }, [isValidRiskSettingsSLTP, validateRiskSettingsTrailSL, validateRiskSettingsProfitLock, validateRiskSettingsProfitLockTrail, dispatch, navigate]);

  const handleTradingLegChange = useCallback((legId, field, value) => {
    setTradingLegs(prev => {
      const updatedLegs = prev.map(leg => {
        if (leg.id !== legId) return leg;
        if (field.startsWith('rms.')) {
          const rmsField = field.split('.')[1];
          if (rmsField === 'stopLossInput' || rmsField === 'takeProfitInput' || rmsField === 'trailSL' || rmsField === 'profitLockTrail') {
            const subField = field.split('.')[2];
            const updatedLeg = {
              ...leg,
              rms: {
                ...leg.rms,
                [rmsField]: {
                  ...leg.rms[rmsField],
                  [subField]: value
                }
              }
            };
            // Validate SL/TP when value changes
            if (rmsField === 'stopLossInput' && subField === 'value') {
              setTimeout(() => {
                isValidLegSLTP(legId, "sl", value, updatedLeg);
              }, 0);
            } else if (rmsField === 'takeProfitInput' && subField === 'value') {
              setTimeout(() => {
                isValidLegSLTP(legId, "tp", value, updatedLeg);
              }, 0);
            }
            return updatedLeg;
          } else if (rmsField === 'basis') {
            if (value === 1) { // Premium
              return {
                ...leg,
                rms: {
                  ...leg.rms,
                  basis: value,
                  stopLossInput: { ...leg.rms.stopLossInput, valueType: 2 },
                  takeProfitInput: { ...leg.rms.takeProfitInput, valueType: 2 }
                }
              };
            }
            if (value === 0) { // PNL
              return {
                ...leg,
                rms: {
                  ...leg.rms,
                  basis: value,
                  stopLossInput: { ...leg.rms.stopLossInput, valueType: 0 },
                  takeProfitInput: { ...leg.rms.takeProfitInput, valueType: 0 }
                }
              };
            }
          } else {
            return {
              ...leg,
              rms: {
                ...leg.rms,
                [rmsField]: value
              }
            };
          }
        }

        if (field === 'strikeSelection') {
          return { ...leg, strikeSelection: value, strikeValue: '' };
        }
        if (field === 'expirySelection' && value === 'Absolute') {
          const first = Array.isArray(absoluteExpiryOptions) && absoluteExpiryOptions.length > 0 ? absoluteExpiryOptions[0] : '';
          return { ...leg, expirySelection: value, expiry: first };
        }
        if (field === 'expirySelection' && value === 'Relative') {
          return { ...leg, expirySelection: value, expiry: 'Current Weekly' };
        }
        return { ...leg, [field]: value };
      });

      if (field === 'qty') {
        const finalUpdatedLegs = updatedLegs.map((leg) => ({
          ...leg,
          oldQty: parseFloat(leg.qty) || 1,
        }));
        setMultiplier(1);
        qtyVal.current = 1;
        console.log("legId", legId, value)
        isValidLegQty(legId, value);
        return finalUpdatedLegs;
      }

      return updatedLegs;
    });
  }, [absoluteExpiryOptions]);

  const fetchStrategyData = useCallback(async (idToFetch) => {
    try {
      const res = await getStrategy(idToFetch);

      if (res?.code === 200 && res?.data) {
        const strategy = res.data;
        const rawBrokers = Array.isArray(strategy.brokers) ? strategy.brokers : (strategy.brokers ? [strategy.brokers] : []);
        const parsedBrokers = rawBrokers.map(b => {
          if (typeof b === 'object' && b !== null && b.id) {
            return { id: b.id, multiplier: b.multiplier || 1 };
          } else {
            return { id: b, multiplier: 1 };
          }
        });

        setFormData(prev => ({
          ...prev,
          brokers: parsedBrokers,
          strategyName: strategyId === idToFetch ? (strategy.name || "") : "",
          description: strategy.description || "",
          symbol: strategy.underlyingInstrument?.underlying || "",
          entryExitMode: strategy.entryExitMode || "signal_based",
          intradayPositional: strategy.positionalOrIntraday ? "positional" : "intraday",
          entryTime: strategy.start || "",
          exitTime: strategy.end || "",
          daysToRun: strategy.daysToRun || [1, 2, 3, 4, 5],
          conditionWait: strategy.nConsecutiveTicks?.toString() || "",
          autoRollOverTime: strategy.autoRollOverTime || "15:29",
          atmSelection: strategy.strikeSelectionType === 0 ? "Spot" :
            strategy.strikeSelectionType === 1 ? "Futures" : "SynthFut",
          entryOrderType: strategy.entryOrderType,
          entryBufferPct: (strategy.entryOrderMarketPercent ?? strategy.entryOrderLimitPercent).toString(),
          exitOrderType: strategy.exitOrderType === "LIMIT" ? "Mkt protect" : "Market",
          exitBufferPct: strategy.exitOrderLimitPercent?.toString() || "5",
          product: strategy.product || "NRML",
          variety: strategy.variety || "REGULAR",
          validity: strategy.validity || "DAY",
          maxLotsPerOrder: strategy.maxLotsPerOrder || 1,
          limitOrderTimeoutAction: strategy.limitOrderTimeoutAction === 0 ? "No Action" :
            strategy.limitOrderTimeoutAction === 1 ? "Convert to Market" : "Cancel",
          limitOrderTimeoutDuration: strategy.limitOrderTimeoutDuration || 5,
          enableReEntryAfterRms: strategy.enableReEntryAfterRms ?? false,
          entryExecutionMode: strategy.entryExecutionMode || "sequential",
          entryBuySellDelaySeconds: strategy.entryBuySellDelaySeconds || 3,
          exitExecutionMode: strategy.exitExecutionMode || "sequential",
          exitBuySellDelaySeconds: strategy.exitBuySellDelaySeconds || 3,
          allOrNothingEntry: strategy.allOrNothingEntry ?? false,
          allOrNothingExit: strategy.allOrNothingExit ?? false,
          rms: {
            basis: strategy.rms?.basis ?? 0,
            stopLossInput: {
              valueType: strategy.rms?.stopLossInput?.valueType || 0,
              value: strategy.rms?.stopLossInput?.value?.toString() || ""
            },
            takeProfitInput: {
              valueType: strategy.rms?.targetInput?.valueType || 0,
              value: strategy.rms?.targetInput?.value?.toString() || ""
            },
            trailSL: {
              valueType: strategy.rms?.trailSL?.valueType || 2,
              reference: strategy.rms?.trailSL?.reference?.toString() || "",
              step: strategy.rms?.trailSL?.step?.toString() || ""
            },
            profitLockAt: strategy.rms?.profitLockAt?.toString() || "",
            profitLockSl: strategy.rms?.profitLockSl?.toString() || "",
            profitLockTrail: {
              valueType: strategy.rms?.profitLockTrail?.valueType || 2,
              reference: strategy.rms?.profitLockTrail?.reference?.toString() || "",
              step: strategy.rms?.profitLockTrail?.step?.toString() || ""
            }
          }
        }));

        if (strategy.legConfigs && Array.isArray(strategy.legConfigs)) {
          const mappedLegs = strategy.legConfigs.map((leg, index) => {
            const relativeExpiryMap = {
              1: 'Current Weekly',
              2: 'Next Weekly',
              3: 'Current Monthly',
              4: 'Next Monthly'
            };

            const strikeSelectionMap = {
              0: 'Absolute',
              1: 'Distance',
              2: 'Prem',
              3: 'Delta'
            };

            let strikeValue = "";
            const optionSelectionType = leg.optionSelectionType || 0;
            if (optionSelectionType === 0) {
              strikeValue = leg.strikePrice?.toString() || "";
            } else if (optionSelectionType === 1) {
              strikeValue = leg.strikeFromAtm?.toString() ?? 0;
            } else if (optionSelectionType === 2) {
              strikeValue = leg.premium?.toString() || "";
            } else if (optionSelectionType === 3) {
              strikeValue = leg.delta?.toString() || "";
            }

            return {
              id: index + 1,
              idle: false,
              side: leg.transactionType === 'BUY' ? 'BUY' : 'SELL',
              futOpt: leg.futOrOpt ? 'FUT' : 'OPT',
              optionType: leg.optionType || 'CE',
              strikeSelection: strikeSelectionMap[leg.optionSelectionType] || 'Distance',
              strikeValue: strikeValue,
              expirySelection: leg.expiryType === 0 ? 'Absolute' : 'Relative',
              expiry: leg.expiryType === 0 ? leg.expiry : relativeExpiryMap[leg.expiryType] || 'Current Monthly',
              qty: leg.lots || 1,
              oldQty: leg.lots || 1,
              onSlAction: "Exit Leg",
              rms: {
                basis: leg.rms.basis,
                stopLossInput: {
                  valueType: leg.rms?.stopLossInput?.valueType || 0,
                  value: (leg.rms?.stopLossInput?.value ?? "").toString()
                },
                takeProfitInput: {
                  valueType: leg.rms?.targetInput?.valueType || 0,
                  value: (leg.rms?.targetInput?.value ?? "").toString()
                },
                trailSL: {
                  valueType: leg.rms?.trailSL?.valueType || 2,
                  reference: (leg.rms?.trailSL?.reference ?? "").toString(),
                  step: (leg.rms?.trailSL?.step ?? "").toString()
                },
                profitLockAt: leg.rms?.profitLockAt,
                profitLockSl: leg.rms?.profitLockSl,
                profitLockTrail: {
                  valueType: leg.rms?.profitLockTrail?.valueType || 2,
                  reference: (leg.rms?.profitLockTrail?.reference ?? "").toString(),
                  step: (leg.rms?.profitLockTrail?.step ?? "").toString()
                }
              },
              onSlAction: leg.onSlAction || 0,
              onSlReEntryCount: leg.onSlReEntryCount || 0,
              onSlTargetStrategyId: leg.onSlTargetStrategyId || "",
              onTgtAction: "Exit Leg"
            };
          });

          setTradingLegs(mappedLegs);
        }

        toast.success("Strategy loaded successfully");
      } else if (res?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate("/authentication/login");
      } else {
        toast.error(res?.error || "Failed to load strategy");
        navigate("/algo/strategies");
      }
    } catch (error) {
      console.error("Error fetching strategy:", error);
      toast.error("Failed to load strategy");
      navigate("/algo/strategies");
    } finally {
    }
  }, [navigate, strategyId]);

  const handleLoadStrategy = useCallback(async () => {
    const id = String(loadStrategyId || "").trim();
    if (!id) {
      toast.error("Enter a strategy ID to load");
      return;
    }
    await fetchStrategyData(id);
  }, [loadStrategyId, fetchStrategyData]);

  useEffect(() => {
    const fetchAllStrategies = async () => {
      try {
        const res = await getStrategies();
        if (res?.code === 200 && Array.isArray(res?.data)) {
          setStrategyList(res.data);
        }
      } catch (e) {
      }
    };
    if (isLoggedIn && brokerLoggedIn) {
      fetchAllStrategies();
    }
  }, [isLoggedIn, brokerLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && brokerLoggedIn && isEditMode && strategyId) {
      fetchStrategyData(strategyId);
    }
  }, [isLoggedIn, brokerLoggedIn, isEditMode, strategyId, fetchStrategyData]);

  const applyStrategyTemplate = useCallback((templateName) => {
    const legs = buildTemplateLegs(templateName, formData.symbol);
    if (!legs.length) return;
    const normalized = legs.map((leg, idx) => ({ ...leg, id: idx + 1 }));
    setTradingLegs(normalized);
  }, [formData.symbol]);

  const addTradingLeg = useCallback(() => {
    const newLeg = {
      id: tradingLegs.length + 1,
      idle: false,
      side: "SELL",
      futOpt: "OPT",
      optionType: "CE",
      strikeSelection: "Distance",
      strikeValue: "0",
      expirySelection: "Relative",
      expiry: "Current Monthly",
      baseQty: 1,
      qty: 1,
      oldQty: 1,
      onSlAction: 0,
      onSlReEntryCount: 0,
      onSlTargetStrategyId: "",
      rms: {
        basis: 0,
        stopLossInput: {
          valueType: 0,
          value: ""
        },
        takeProfitInput: {
          valueType: 0,
          value: ""
        },
        trailSL: {
          valueType: 2,
          reference: "",
          step: ""
        },
        profitLockAt: "",
        profitLockSl: "",
        profitLockTrail: {
          valueType: 2,
          reference: "",
          step: ""
        }
      },
      onTgtAction: "Exit Leg"
    };
    setTradingLegs(prev => [...prev, newLeg]);
  }, [tradingLegs]);

  const removeTradingLeg = useCallback((legId) => {
    if (tradingLegs.length > 1) {
      setTradingLegs(prev => {
        const filteredLegs = prev.filter(leg => leg.id !== legId);
        return filteredLegs.map((leg, index) => ({
          ...leg,
          id: index + 1
        }));
      });
    }
  }, [tradingLegs.length]);

  const copyTradingLeg = useCallback((legId) => {
    setTradingLegs(prev => {
      const legToCopy = prev.find(leg => leg.id === legId);
      if (legToCopy) {
        const newLeg = {
          ...legToCopy,
          id: prev.length + 1
        };
        toast.success(`Leg ${legId} copied successfully`);
        return [...prev, newLeg];
      }
      return prev;
    });
  }, []);

  const reorderLegs = useCallback((fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
    setTradingLegs(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated.map((leg, idx) => ({ ...leg, id: idx + 1 }));
    });
  }, []);

  const updateMultiplier = useCallback((val) => {
    setMultiplier(val);
    const newDataArray = tradingLegs.map((leg) => {
      const baseLots = leg?.oldQty && parseFloat(leg.oldQty) > 0
        ? parseFloat(leg.oldQty)
        : parseFloat(leg.qty) || 1;
      const newLots = baseLots * val;

      return {
        ...leg,
        qty: Math.max(1, Math.floor(newLots)),
      };
    });
    setTradingLegs(newDataArray);
    qtyVal.current = val;
  }, [tradingLegs]);


  // Validate trading legs when they change
  useEffect(() => {
    tradingLegs.forEach(leg => {
      const slValue = leg.rms.stopLossInput.value;
      const tpValue = leg.rms.takeProfitInput.value;
      if (slValue !== null && slValue !== undefined && slValue !== '' && slValue !== 0) {
        isValidLegSLTP(leg.id, "sl", slValue, leg);
      }
      if (tpValue !== null && tpValue !== undefined && tpValue !== '' && tpValue !== 0) {
        isValidLegSLTP(leg.id, "tp", tpValue, leg);
      }
      isValidLegQty(leg.id, leg.qty);
    });
  }, [tradingLegs.map(leg => `${leg.id}-${leg.qty}-${leg.rms.basis}-${leg.rms.stopLossInput.value}-${leg.rms.takeProfitInput.value}`).join(',')]);

  useEffect(() => {
    const basis = formData.rms?.basis;
    const slValue = formData.rms?.stopLossInput?.value;
    const tpValue = formData.rms?.takeProfitInput?.value;
    if (slValue !== null && slValue !== undefined && slValue !== '' && slValue !== 0) {
      isValidRiskSettingsSLTP("sl", slValue, basis);
    }
    if (tpValue !== null && tpValue !== undefined && tpValue !== '' && tpValue !== 0) {
      isValidRiskSettingsSLTP("tp", tpValue, basis);
    }
    validateRiskSettingsTrailSL(formData.rms?.trailSL?.reference, formData.rms?.trailSL?.step);
    validateRiskSettingsProfitLock(formData.rms?.profitLockAt, formData.rms?.profitLockSl);
    validateRiskSettingsProfitLockTrail(formData.rms?.profitLockTrail?.reference, formData.rms?.profitLockTrail?.step);
  }, [formData?.rms]);


  const validateForm = useCallback(() => {
    const requiredFields = formData.intradayPositional === 'intraday'
      ? ['strategyName', 'symbol', 'entryTime', 'exitTime']
      : ['strategyName', 'symbol'];
    const isValid = requiredFields.every(field => formData[field]);
    setIsFormValid(isValid);
    return isValid;
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setTradingLegs(INITIAL_TRADING_LEGS);
    setMultiplier(1);
    qtyVal.current = 1;
    setSavedStrategyId("");
    setIsSavedModalOpen(false);
  }, []);

  const handleSaveStrategy = useCallback(async () => {
    const nameMissing = !String(formData.strategyName || '').trim();
    const strikeMissing = (tradingLegs || []).some((leg) => {
      if (leg.futOpt === 'FUT') return false;
      const requiresStrike = ['Absolute', 'Distance', 'Prem', 'Delta'].includes(leg.strikeSelection);
      if (!requiresStrike) return false;
      return leg.strikeValue === '' || leg.strikeValue === null || leg.strikeValue === undefined;
    });

    if (nameMissing && strikeMissing) {
      toast.error('Please enter strategy name and strike value for all option legs');
      return;
    }
    if (nameMissing) {
      toast.error('Please enter strategy name');
      return;
    }
    if (strikeMissing) {
      toast.error('Please enter strike value for all option legs');
      return;
    }

    if (formData.entryExitMode === 'time_based' && (!formData.brokers || formData.brokers.length === 0)) {
      toast.error('Please link at least one broker for time-based strategy');
      return;
    }
    if (isEditMode) {
      const userConfirmed = await new Promise((resolve) => {
        confirm({
          title: '',
          icon: null,
          content: `Strategy Modifications will take effect when the next new signal is received. If you want to modify a strategy with live positions, use the Accounts Dashboard to modify respective Strategy Baskets.`,
          okText: 'Continue',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!userConfirmed) return;
    }
    try {
      setSaving(true);
      let underlyingInstrumentToken = 0;
      try {
        const spotRes = await getSpotToken(formData.symbol, formData.symbol == "SENSEX" || formData.symbol == "BANKEX"
          ? "BSE"
          : "NSE");
        if (spotRes?.code === 200 && Array.isArray(spotRes?.data) && spotRes.data[0]?.instrumentToken) {
          underlyingInstrumentToken = spotRes.data[0].instrumentToken;
        }
      } catch (e) { }

      const strikeSelectionType = formData.atmSelection === 'Spot' ? 0 : (formData.atmSelection === 'Futures' ? 1 : 2);
      const daysToRun = Array.isArray(formData.daysToRun) && formData.daysToRun.length > 0 ? formData.daysToRun : [0, 1, 2, 3, 4, 5, 6];

      const legConfigs = tradingLegs.map(leg => {
        const relativeExpiryTypeMap = {
          'Current Weekly': 1,
          'Next Weekly': 2,
          'Current Monthly': 3,
          'Next Monthly': 4,
        };
        const isFut = leg.futOpt === 'FUT';

        const basis = leg.rms.basis;

        const commonFields = {
          lots: Number(leg?.qty),
          transactionType: leg.side === 'BUY' ? 'BUY' : 'SELL',
          futOrOpt: isFut,
          expiryType: leg.expirySelection === 'Absolute' ? 0 : (relativeExpiryTypeMap[leg.expiry] ?? 1),
          expiry: leg.expirySelection === 'Absolute' ? (leg.expiry || null) : null,
          rms: {
            basis: basis,
            targetInput: (leg.rms.takeProfitInput.value === null || leg.rms.takeProfitInput.value === undefined || Number.isNaN(Number(leg.rms.takeProfitInput.value)) || Number(leg.rms.takeProfitInput.value) === 0)
              ? null
              : { valueType: leg.rms.takeProfitInput.valueType, value: Number(leg.rms.takeProfitInput.value) },
            stopLossInput: (leg.rms.stopLossInput.value === null || leg.rms.stopLossInput.value === undefined || Number.isNaN(Number(leg.rms.stopLossInput.value)) || Number(leg.rms.stopLossInput.value) === 0)
              ? null
              : { valueType: leg.rms.stopLossInput.valueType, value: Number(leg.rms.stopLossInput.value) },
            trailSL: (leg.rms.trailSL.reference || leg.rms.trailSL.step)
              ? { valueType: leg.rms.trailSL.valueType, reference: Number(leg.rms.trailSL.reference) || null, step: Number(leg.rms.trailSL.step) || null }
              : null,
            profitLockAt: (leg.rms.profitLockAt === "" || leg.rms.profitLockAt === null || leg.rms.profitLockAt === undefined) ? null : Number(leg.rms.profitLockAt),
            profitLockSl: (leg.rms.profitLockSl === "" || leg.rms.profitLockSl === null || leg.rms.profitLockSl === undefined) ? null : Number(leg.rms.profitLockSl),
            profitLockTrail: (leg.rms.profitLockTrail.reference || leg.rms.profitLockTrail.step)
              ? {
                valueType: 2,
                reference: leg.rms.profitLockTrail.reference ? Number(leg.rms.profitLockTrail.reference) : null,
                step: leg.rms.profitLockTrail.step ? Number(leg.rms.profitLockTrail.step) : null
              }
              : null
          },
          onSlAction: Number(leg.onSlAction) || 0,
          onSlReEntryCount: Number(leg.onSlReEntryCount) || 0,
          onSlTargetStrategyId: leg.onSlTargetStrategyId || ""
        };

        if (isFut) {
          return {
            ...commonFields,
            optionType: '',
          };
        }

        const optionSelectionTypeMap = {
          'Absolute': 0,
          'Distance': 1,
          'Prem': 2,
          'Delta': 3
        };

        const optionSelectionType = optionSelectionTypeMap[leg.strikeSelection] || 0;

        const optionFields = {
          optionType: (leg.optionType === 'PE' ? 'PE' : 'CE'),
          optionSelectionType,
          strikePrice: 0,
          strikeFromAtm: 0,
          premium: 0.0,
          delta: 0.0
        };

        if (optionSelectionType === 0) {
          optionFields.strikePrice = Number(leg.strikeValue) || 0;
        } else if (optionSelectionType === 1) {
          optionFields.strikeFromAtm = Number(leg.strikeValue) || 0;
        } else if (optionSelectionType === 2) {
          optionFields.premium = Number(leg.strikeValue) || 0.0;
        } else if (optionSelectionType === 3) {
          optionFields.delta = Number(leg.strikeValue) || 0.0;
        }

        return {
          ...commonFields,
          ...optionFields,
        };
      });

      const mapOrderType = (t) => (t === 'Mkt protect' ? 'LIMIT' : 'MARKET');


      const payload = {
        brokers: formData.brokers || [],
        name: formData.strategyName,
        description: formData.description,
        strikeSelectionType,
        underlyingInstrumentToken,
        nConsecutiveTicks: Number(formData.conditionWait) || 0,
        autoRollOverTime: formData.intradayPositional === 'positional' ? formData.autoRollOverTime : null,
        ...(formData.intradayPositional === 'intraday' ? {
          start: formData.entryTime?.replace(/^[^0-9]*/, ''),
          end: formData.exitTime?.replace(/^[^0-9]*/, ''),
        } : null),
        daysToRun: daysToRun,
        product: formData.product || "NRML",
        variety: formData.variety || "REGULAR",
        validity: formData.validity || "DAY",
        entryOrderType: formData.entryOrderType,
        entryOrderMarketPercent: formData.entryOrderType === 'Mkt protect' ? Number(formData.entryBufferPct) : null,
        entryOrderLimitPercent: formData.entryOrderType === 'Limit with buffer' ? Number(formData.entryBufferPct) : null,
        exitOrderType: mapOrderType(formData.exitOrderType),
        exitOrderLimitPercent: formData.exitOrderType === 'Mkt protect' ? Number(formData.exitBufferPct) : null,
        maxLotsPerOrder: formData.maxLotsPerOrder || null,
        limitOrderTimeoutAction: formData.limitOrderTimeoutAction === "Convert to Market" ? 1 : formData.limitOrderTimeoutAction === "Cancel" ? 2 : 0,
        limitOrderTimeoutDuration: Number(formData.limitOrderTimeoutDuration) || 5,
        enableReEntryAfterRms: formData.enableReEntryAfterRms,
        entryExecutionMode: formData.entryExecutionMode || "sequential",
        entryBuySellDelaySeconds: formData.entryExecutionMode === "parallel" ? null : (Number(formData.entryBuySellDelaySeconds) || 3),
        exitExecutionMode: formData.exitExecutionMode || "sequential",
        exitBuySellDelaySeconds: formData.exitExecutionMode === "parallel" ? null : (Number(formData.exitBuySellDelaySeconds) || 3),
        allOrNothingEntry: formData.allOrNothingEntry,
        allOrNothingExit: formData.allOrNothingExit,
        positionalOrIntraday: formData.intradayPositional === 'positional',
        entryExitMode: formData.entryExitMode,
        rms: {
          basis: (formData.rms.basis),
          targetInput: (formData.rms.takeProfitInput.value === '' || formData.rms.takeProfitInput.value === null || formData.rms.takeProfitInput.value === undefined)
            ? null
            : { valueType: formData.rms.takeProfitInput.valueType, value: Number(formData.rms.takeProfitInput.value) },
          stopLossInput: (formData.rms.stopLossInput.value === '' || formData.rms.stopLossInput.value === null || formData.rms.stopLossInput.value === undefined)
            ? null
            : { valueType: formData.rms.stopLossInput.valueType, value: Number(formData.rms.stopLossInput.value) },
          trailSL: (formData.rms.trailSL.reference === '' && formData.rms.trailSL.step === '')
            ? null
            : {
              valueType: (formData.rms.basis === 0) ? 2 : (formData.rms.trailSL.valueType === 1 ? 1 : 2),
              reference: formData.rms.trailSL.reference ? Number(formData.rms.trailSL.reference) : null,
              step: formData.rms.trailSL.step ? Number(formData.rms.trailSL.step) : null
            },
          profitLockAt: formData.rms.profitLockAt === '' || formData.rms.profitLockAt === null || formData.rms.profitLockAt === undefined ? null : Number(formData.rms.profitLockAt),
          profitLockSl: formData.rms.profitLockSl === '' || formData.rms.profitLockSl === null || formData.rms.profitLockSl === undefined ? null : Number(formData.rms.profitLockSl),
          profitLockTrail: (formData.rms.profitLockTrail.reference || formData.rms.profitLockTrail.step)
            ? { valueType: formData.rms.profitLockTrail.valueType, reference: formData.rms.profitLockTrail.reference ? Number(formData.rms.profitLockTrail.reference) : null, step: formData.rms.profitLockTrail.step ? Number(formData.rms.profitLockTrail.step) : null }
            : null,
          perLot: formData.rms.basis === 1 ? !!formData.rms.perLot : false
        },
        legConfigs,
      };

      const res = isEditMode ? await apiUpdateStrategy(strategyId, payload) : await apiCreateStrategy(payload);
      if (res?.code === 200) {
        setSavedStrategyId(res?.data?.id || "");
        if (!isEditMode) {
          setIsSavedModalOpen(true);
        }
        toast.success(isEditMode ? "Strategy updated successfully" : "Strategy saved successfully");
      } else if (res?.sessionExpired) {
        toast.error("Session expired. Please login again.");
        navigate('/authentication/login');
      } else {
        toast.error(res?.error || (isEditMode ? 'Failed to update strategy' : 'Failed to save strategy'));
      }
    } catch (err) {
      toast.error(isEditMode ? 'Failed to update strategy' : 'Failed to save strategy');
    } finally {
      setSaving(false);
    }
  }, [formData, tradingLegs, navigate, validateForm]);

  return (
    <div className="quiktrade-algo">

      {/* Main Content */}
      <div className="algo-content">
        <div className="create-strategy-container">
          <div className="config-section">
            <div className="qt-algo-section-header">
              <Settings2 size={20} />
              <h3>
                Strategy Configuration
              </h3>
            </div>
            <div className="config-grid">
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div className="config-item">
                    <label>Strategy Name</label>
                    <Input
                      placeholder="Strategy name"
                      value={formData.strategyName}
                      onChange={(e) => handleInputChange('strategyName', e.target.value)}
                      size="large"
                      style={{ width: 320, height: 40 }}
                    />
                  </div>
                  <Button
                    icon={<FileText size={16} />}
                    onClick={() => setIsDescriptionModalOpen(true)}
                    style={{ height: 40 }}
                  >
                    {formData.description ? 'Edit Description' : 'Add Description'}
                  </Button>
                </div>
                <div style={{ display: "flex", alignItems: "end", gap: "12px" }}>
                  <div className="config-item">
                    <label>Load Strategy <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.loadStrategy}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <Select
                      showSearch
                      placeholder={strategyList.length ? "Select a strategy" : "No strategies"}
                      value={loadStrategyId || undefined}
                      onChange={(v) => setLoadStrategyId(v)}
                      style={{ width: 260 }}
                      size={"middle"}
                      filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {strategyList.map((s) => (
                        <Option key={s.id} value={String(s.id)}>{s.name}</Option>
                      ))}
                    </Select>
                  </div>
                  <Button type="primary" className="btn-apply" size="large" onClick={handleLoadStrategy} style={{ height: 40 }}>
                    Load
                  </Button>
                </div>
              </div>
              <div className="config-item">
                <label>Symbol</label>
                <Select
                  showSearch
                  placeholder={isLoadingInstruments ? "Loading instruments..." : "Select instrument"}
                  value={formData.symbol}
                  onChange={(value) => handleInputChange('symbol', value)}
                  disabled={isEditMode}
                  loading={isLoadingInstruments}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: 140 }}
                  size={"middle"}
                  notFoundContent={instrumentOptions.length === 0 ? "No instruments found" : "No matching instruments"}
                >
                  {instrumentOptions.map(option => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="config-item">
                <label>Signal / Time Based <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.entryExitMode}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                <div className="toggle-container">
                  <button
                    className={`toggle-btn ${formData.entryExitMode === 'signal_based' ? 'active' : ''}`}
                    onClick={() => handleInputChange('entryExitMode', 'signal_based')}
                  >
                    Signal
                  </button>
                  <button
                    className={`toggle-btn ${formData.entryExitMode === 'time_based' ? 'active' : ''}`}
                    onClick={() => handleInputChange('entryExitMode', 'time_based')}
                  >
                    Time
                  </button>
                </div>
              </div>

              <div className="config-item">
                <label>Intraday/Positional <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.intradayPositional}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                <div className="toggle-container">
                  <button
                    className={`toggle-btn ${formData.intradayPositional === 'intraday' ? 'active' : ''}`}
                    onClick={() => handleInputChange('intradayPositional', 'intraday')}
                  >
                    Intraday
                  </button>
                  <button
                    className={`toggle-btn ${formData.intradayPositional === 'positional' ? 'active' : ''}`}
                    disabled={formData.entryExitMode === 'time_based'}
                    onClick={() => handleInputChange('intradayPositional', 'positional')}
                    style={formData.entryExitMode === 'time_based' ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                  >
                    Positional
                  </button>
                </div>
              </div>

              {formData.intradayPositional === 'intraday' && (
                <div className="config-item">
                  <label>Entry Time <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.entryExitTime}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                  <Input
                    type="time"
                    value={formData.entryTime}
                    onChange={(e) => handleInputChange('entryTime', e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === "Backspace" || e.key === "Delete")) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Select entry time"
                  />
                </div>
              )}

              {formData.intradayPositional === 'intraday' && (
                <div className="config-item">
                  <label>Exit Time <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.entryExitTime}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                  <Input
                    type="time"
                    value={formData.exitTime}
                    onChange={(e) => handleInputChange('exitTime', e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === "Backspace" || e.key === "Delete")) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Select exit time"
                  />
                </div>
              )}


              <div className="config-item">
                <label>Days to Run <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.daysToRun}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                <Select
                  mode="multiple"
                  placeholder="Select days"
                  value={formData.daysToRun}
                  onChange={(value) => handleInputChange('daysToRun', value)}
                  style={{ minWidth: 220 }}
                  size={"middle"}
                >
                  <Option value={0}>Sun</Option>
                  <Option value={1}>Mon</Option>
                  <Option value={2}>Tue</Option>
                  <Option value={3}>Wed</Option>
                  <Option value={4}>Thu</Option>
                  <Option value={5}>Fri</Option>
                  <Option value={6}>Sat</Option>
                </Select>
              </div>

              <div className="config-item">
                <label>ATM Selection Method <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.atmSelection}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                <Select
                  value={formData.atmSelection}
                  onChange={(value) => handleInputChange('atmSelection', value)}
                  style={{ width: 140 }}
                  size={"middle"}
                >
                  <Option value="Spot">Spot</Option>
                  <Option value="Futures">Futures</Option>
                  <Option value="SynthFut">SynthFut</Option>
                </Select>
              </div>



              <div className="config-item">
                <label>Strategy Templates <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.strategyTemplates}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                <Select
                  value={formData.strategyTemplate}
                  onChange={(value) => {
                    handleInputChange('strategyTemplate', value);
                    applyStrategyTemplate(value);
                  }}
                  placeholder="Select Template"
                  style={{ width: 180 }}
                  size={"middle"}
                >
                  {strategyTemplates.map((group) => (
                    <OptGroup key={group.group} label={group.group}>
                      {group.templates.map((template) => (
                        <Option key={template.value} value={template.value}>
                          {template.label}
                        </Option>
                      ))}
                    </OptGroup>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <StrategyLegs
            tradingLegs={tradingLegs}
            symbol={formData.symbol}
            multiplier={multiplier}
            updateMultiplier={updateMultiplier}
            handleTradingLegChange={handleTradingLegChange}
            addTradingLeg={addTradingLeg}
            copyTradingLeg={copyTradingLeg}
            removeTradingLeg={removeTradingLeg}
            reorderLegs={reorderLegs}
            absoluteExpiryOptions={absoluteExpiryOptions}
            atmStrike={atmStrike}
            strikeListCache={strikeListCache}
            fetchStrikeList={fetchStrikeList}
            legErrorMessages={legErrorMessages}
            strategyList={strategyList}
            isEditMode={isEditMode}
            strategyId={strategyId}
          />

          {/* Strategy Settings - Stacked Sections */}
          <div className="config-section">
            <div className="qt-algo-section-header">
              <Settings size={20} />
              <h3>
                Strategy Settings
              </h3>
            </div>
            <div className="execution-content">
              <div className="execution-column">
                <h4>Order Attributes</h4>
                <div className="setting-items">
                  <div className="setting-item">
                    <label>Product <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.product}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <Select value={formData.product} onChange={(v) => handleInputChange('product', v)} style={{ width: '100%' }} >
                      <Option value="NRML">NRML</Option>
                      <Option value="MIS">MIS</Option>
                    </Select>
                  </div>
                  <div className="setting-row">
                    <div style={{ width: 175 }} className="setting-item">
                      <label>Entry Order <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.orderType}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                      <Select
                        value={formData.entryOrderType}
                        onChange={(v) => handleInputChange('entryOrderType', v)}
                        style={{ width: '100%' }}
                      >
                        <Option value="Mkt protect">Mkt protect</Option>
                        <Option value="Limit at LTP">Limit at LTP</Option>
                        <Option value="Limit with buffer">Limit with buffer</Option>
                      </Select>
                    </div>
                    {formData.entryOrderType !== 'Limit at LTP' && (
                      <div className="setting-item">
                        <label style={{ whiteSpace: "nowrap" }}>
                          {formData.entryOrderType === 'Mkt protect' ? 'Entry Market Protect %' : 'Entry Market Buffer %'}
                          <Tooltip overlayStyle={{ position: 'fixed' }} title={formData.entryOrderType === 'Mkt protect' ? helpText.marketProtect : helpText.limitWithBuffer}>
                            <Info size={12} style={{ marginLeft: 4, cursor: 'help' }} />
                          </Tooltip>
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.entryBufferPct}
                          onChange={(e) => handleInputChange('entryBufferPct', e.target.value)}
                          placeholder="e.g. 0.25"
                          style={{ width: '100px' }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="setting-row">
                    <div style={{ width: 175 }} className="setting-item">
                      <label>Exit Order <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.exitOrderType}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                      <Select
                        value={formData.exitOrderType}
                        onChange={(v) => handleInputChange('exitOrderType', v)}
                        style={{ width: '100%' }}
                      >
                        <Option value="Market">Market</Option>
                        <Option value="Mkt protect">Market Protect (LIMIT)</Option>
                      </Select>
                    </div>
                    {formData.exitOrderType === 'Mkt protect' && (
                      <div className="setting-item" style={{ width: 100 }}>
                        <label style={{ whiteSpace: "nowrap" }}>
                          Exit Market Protect %
                          <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.marketProtect}>
                            <Info size={12} style={{ marginLeft: 4, cursor: 'help' }} />
                          </Tooltip>
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.exitBufferPct}
                          onChange={(e) => handleInputChange('exitBufferPct', e.target.value)}
                          placeholder="e.g. 0.25"
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="setting-item">
                    <label>Max Qty per Order <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.maxQtyPerOrder}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <PlusMinQty
                      qtyClick={(qty) => {
                        if (optChainData?.volumeFreeze == 0) {
                          if ((qty * optChainData?.lotSize) > 50 * optChainData?.lotSize) {
                            return false
                          } else {
                            handleInputChange('maxLotsPerOrder', Number(qty))
                          }
                        } else {
                          if ((qty * optChainData?.lotSize) > optChainData?.volumeFreeze) {
                            return
                          } else {
                            handleInputChange('maxLotsPerOrder', Number(qty))
                          }
                        }
                      }}
                      filterValue={formData.maxLotsPerOrder}
                      name={"freezeQty"}
                      step={1}
                      max={optChainData?.volumeFreeze == 0 ? 50 : Math.floor(optChainData?.volumeFreeze / optChainData?.lotSize)}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Order Priority - Sell First</label>
                    <Switch
                      checked={sellFirst}
                      onChange={handleSellFirstToggle}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                  </div>
                </div>
              </div>

              <div className="execution-column">
                <h4>Risk Settings</h4>
                <div className="setting-items">
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ width: '175px' }} className="setting-item">
                      <label>SL/TP Basis</label>
                      <Select
                        value={formData.rms.basis === 1 ? "Comb Prem" : "PNL"}
                        onChange={(v) => handleInputChange('basis', v === "Comb Prem" ? 1 : 0)}
                        style={{ width: '100%' }}
                      >
                        <Option value="Comb Prem">Combined Premium</Option>
                        <Option value="PNL">PNL</Option>
                      </Select>
                    </div>

                    {formData.rms.basis === 1 && (
                      <div className="setting-item">
                        <label>1 Lot RMS <Tooltip overlayStyle={{ position: 'fixed' }} title="If enabled, combined premium ignores quantity, each leg counts as a single lot."><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                        <Switch
                          checked={formData.rms.perLot}
                          onChange={(checked) => handleInputChange('rms.perLot', checked)}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ width: '100%' }} className="setting-item">
                    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                      {/* Stop Loss Section */}
                      <div className="setting-item">
                        <div className="setting-subh">Stop Loss</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div className={riskSettingsErrorMessages.stopLoss ? "error-border" : ""}>
                              <PlusMinQty
                                qtyClick={(qty) => handleInputChange('rms.stopLossInput.value', qty)}
                                filterValue={formData.rms.stopLossInput.value || null}
                                name={"stopLossAbsolute"}
                                min={null}
                                step={0.05}
                                max={null}
                                title={""}
                                formatINR={true}
                              />
                            </div>
                            {formData?.rms?.basis === 0 ? (
                              <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(formData.rms.stopLossInput.valueType)}</div>
                            ) : (
                              <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(formData.rms.stopLossInput.valueType)}</div>
                            )}
                          </div>
                          {riskSettingsErrorMessages.stopLoss && (
                            <div className="text-danger" style={{ fontSize: '10px' }}>{riskSettingsErrorMessages.stopLoss}</div>
                          )}
                        </div>
                      </div>

                      {/* Target Profit Section */}
                      <div className="setting-item">
                        <div className="setting-subh">Target</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div className={riskSettingsErrorMessages.target ? "error-border" : ""}>
                              <PlusMinQty
                                qtyClick={(qty) => handleInputChange('rms.takeProfitInput.value', qty)}
                                filterValue={formData.rms.takeProfitInput.value || null}
                                name={"takeProfitAbsolute"}
                                min={null}
                                step={0.05}
                                max={null}
                                title={""}
                                formatINR={true}
                              />
                            </div>
                            {formData?.rms?.basis === 0 ? (
                              <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(formData.rms.takeProfitInput.valueType)}</div>
                            ) : (
                              <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(formData.rms.takeProfitInput.valueType)}</div>
                            )}
                          </div>
                          {riskSettingsErrorMessages.target && (
                            <div className="text-danger" style={{ fontSize: '10px' }}>{riskSettingsErrorMessages.target}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trailing SL Section */}
                  <div className="setting-item">
                    <div className="setting-subh">Trailing SL</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem", alignItems: "end" }}>
                        <div className="setting-item">
                          <label>For every X move</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.trailSL.reference', qty)}
                              filterValue={formData.rms.trailSL.reference || null}
                              name={"trailReferenceAbsolute"}
                              min={0.05}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                              disabled={!formData.rms.stopLossInput.value || formData.rms.stopLossInput.value === null || formData.rms.stopLossInput.value === '' || formData.rms.stopLossInput.value === 0}
                            />
                            {formData?.rms?.basis === 0 ? (
                              <div className="pts-class">{renderPNLLabel()}</div>
                            ) : (
                              <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(formData.rms.trailSL.valueType)}</div>
                            )}
                          </div>
                        </div>
                        <div className="setting-item">
                          <label>Trail SL by Y</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.trailSL.step', qty)}
                              filterValue={formData.rms.trailSL.step || null}
                              name={"trailStepAbsolute"}
                              min={0.05}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                              disabled={!formData.rms.stopLossInput.value || formData.rms.stopLossInput.value === null || formData.rms.stopLossInput.value === '' || formData.rms.stopLossInput.value === 0}
                            />
                            {formData?.rms?.basis === 0 ? (
                              <div className="pts-class">{renderPNLLabel()}</div>
                            ) : (
                              <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(formData.rms.trailSL.valueType)}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      {riskSettingsErrorMessages.trailSL && (
                        <div className="text-danger" style={{ fontSize: '10px' }}>{riskSettingsErrorMessages.trailSL}</div>
                      )}
                    </div>
                  </div>

                  {/* Profit Locking Section */}
                  <div className="setting-item">
                    <div className="setting-subh">Profit Locking</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div className="setting-item">
                          <label>When profit reaches</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.profitLockAt', qty)}
                              filterValue={formData.rms.profitLockAt || null}
                              name={"profitLockAt"}
                              min={null}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                            />
                            <div className="pts-class">{renderPNLLabel()}</div>
                          </div>
                        </div>
                        <div className="setting-item">
                          <label>Lock minimum profit at</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.profitLockSl', qty)}
                              filterValue={formData.rms.profitLockSl || null}
                              name={"profitLockSl"}
                              min={null}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                            />
                            <div className="pts-class">{renderPNLLabel()}</div>
                          </div>
                        </div>
                      </div>
                      {riskSettingsErrorMessages.profitLock && (
                        <div className="text-danger" style={{ fontSize: '10px' }}>{riskSettingsErrorMessages.profitLock}</div>
                      )}
                    </div>
                  </div>

                  {/* Profit Trailing Section */}
                  <div className="setting-item">
                    <div className="setting-subh">Profit Trailing</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <div className="setting-item">
                          <label>If profit increases by X</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.profitLockTrail.reference', qty)}
                              filterValue={formData.rms.profitLockTrail.reference || null}
                              name={"profitLockTrailRef"}
                              min={0.05}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                              disabled={formData.rms.profitLockAt === null || formData.rms.profitLockAt === '' || formData.rms.profitLockAt === 0 || formData.rms.profitLockSl === null || formData.rms.profitLockSl === '' || formData.rms.profitLockSl === 0}
                            />
                            <div className="pts-class">{renderPNLLabel()}</div>
                          </div>
                        </div>
                        <div className="setting-item">
                          <label>Trail Profit by Y</label>
                          <div style={{ display: 'flex', alignItems: "center" }}>
                            <PlusMinQty
                              qtyClick={(qty) => handleInputChange('rms.profitLockTrail.step', qty)}
                              filterValue={formData.rms.profitLockTrail.step || null}
                              name={"profitLockTrailStep"}
                              min={0.05}
                              step={0.05}
                              max={null}
                              title={""}
                              formatINR={true}
                              disabled={formData.rms.profitLockAt === null || formData.rms.profitLockAt === '' || formData.rms.profitLockAt === 0 || formData.rms.profitLockSl === null || formData.rms.profitLockSl === '' || formData.rms.profitLockSl === 0}
                            />
                            <div className="pts-class">{renderPNLLabel()}</div>
                          </div>
                        </div>
                      </div>
                      {riskSettingsErrorMessages.profitLockTrail && (
                        <div className="text-danger" style={{ fontSize: '10px' }}>{riskSettingsErrorMessages.profitLockTrail}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="execution-column">
                <h4>Execution Settings</h4>
                <div className="setting-items">
                  <div className="setting-item">
                    <label>Limit Timeout Action <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.limitTimeoutAction}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <Select
                      value={formData.limitOrderTimeoutAction}
                      onChange={(v) => handleInputChange('limitOrderTimeoutAction', v)}
                      style={{ width: '175px' }}
                    >
                      <Option value="No Action">No Action</Option>
                      <Option value="Cancel">Cancel</Option>
                      <Option value="Convert to Market">Convert to Market</Option>
                    </Select>
                  </div>
                  <div className="setting-item">
                    <label>Limit Timeout Wait <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.limitTimeoutSecs}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="Enter timeout in seconds"
                      value={formData.limitOrderTimeoutDuration}
                      onChange={(e) => handleInputChange('limitOrderTimeoutDuration', e.target.value)}
                      style={{ width: '175px' }}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Condition Wait <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.waitXTicks}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                    <Input
                      type="number"
                      placeholder="X seconds"
                      value={formData.conditionWait}
                      onChange={(e) => handleInputChange('conditionWait', e.target.value)}
                      style={{ width: '175px' }}
                    />
                  </div>
                  {formData.intradayPositional === 'positional' && (
                    <div className="setting-item">
                      <label>Auto Rollover Time <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.autoSquareOff}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                      <Input
                        type="time"
                        value={formData.autoRollOverTime}
                        onChange={(e) => handleInputChange('autoRollOverTime', e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === "Backspace" || e.key === "Delete")) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="Select auto rollover time"
                        style={{ width: '175px' }}
                      />
                    </div>
                  )}
                  <div className="setting-item">
                    <label>Re-Entry after Strat SL-TP Reached</label>
                    <Switch
                      checked={formData.enableReEntryAfterRms}
                      onChange={(checked) => handleInputChange('enableReEntryAfterRms', checked)}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                  </div>
                  <div className="setting-items">
                    <h5>Multi legs options execution</h5>
                    <div className="setting-row" style={{ gap: '12px', marginBottom: '8px' }}>
                      <div className="setting-item" style={{ flex: 1 }}>
                        <label>Entry Mode <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.executionMode}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                        <Select
                          value={formData.entryExecutionMode}
                          onChange={(v) => handleInputChange('entryExecutionMode', v)}
                          style={{ width: '175px' }}
                        >
                          {defExecutionMode.map((mode) => (
                            <Option key={mode} value={mode}>
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      {formData.entryExecutionMode !== 'parallel' && (
                        <div className="setting-item" style={{ flex: 1 }}>
                          <label>Delay (Secs) <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.buySellDelay}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Secs"
                            value={formData.entryBuySellDelaySeconds}
                            onChange={(e) => handleInputChange('entryBuySellDelaySeconds', e.target.value)}
                            style={{ width: '175px' }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="setting-row" style={{ gap: '12px' }}>
                      <div className="setting-item" style={{ flex: 1 }}>
                        <label>Exit Mode <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.executionMode}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                        <Select
                          value={formData.exitExecutionMode}
                          onChange={(v) => handleInputChange('exitExecutionMode', v)}
                          style={{ width: '175px' }}
                        >
                          {defExecutionMode.map((mode) => (
                            <Option key={mode} value={mode}>
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      {formData.exitExecutionMode !== 'parallel' && (
                        <div className="setting-item" style={{ flex: 1 }}>
                          <label>Delay (Secs) <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.buySellDelay}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip></label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Secs"
                            value={formData.exitBuySellDelaySeconds}
                            onChange={(e) => handleInputChange('exitBuySellDelaySeconds', e.target.value)}
                            style={{ width: '175px' }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="setting-row" style={{ gap: '12px', marginTop: '8px' }}>
                      <div className="setting-item" style={{ flex: 1 }}>
                        <label>
                          Exit on Margin Error (Entry) <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.allOrNothing}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip>
                        </label>
                        <Switch
                          checked={formData.allOrNothingEntry}
                          onChange={(checked) => handleInputChange('allOrNothingEntry', checked)}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                        />
                      </div>
                      <div className="setting-item" style={{ flex: 1 }}>
                        <label>
                          Exit on Margin Error (Exit) <Tooltip overlayStyle={{ position: 'fixed' }} title={helpText.allOrNothing}><Info size={12} style={{ marginLeft: 4, cursor: 'help' }} /></Tooltip>
                        </label>
                        <Switch
                          checked={formData.allOrNothingExit}
                          onChange={(checked) => handleInputChange('allOrNothingExit', checked)}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {formData.entryExitMode === 'time_based' && (
            <LinkBrokers
              brokers={brokers}
              selectedBrokers={formData.brokers}
              onChange={(newBrokers) => handleInputChange('brokers', newBrokers)}
              getBrokerLabel={getBrokerLabel}
            />
          )}

          {/* Footer Buttons */}
          <div className="footer-buttons">
            <Button
              size="large"
              onClick={() => {
                resetForm();
                toast.success("Form reset successfully");
              }}
              className="btn-reset"
            >
              Reset
            </Button>
            <Button
              type="primary"
              size="large"
              loading={saving}
              onClick={handleSaveStrategy}
              className="btn-apply"
            >
              Save Strategy
            </Button>
          </div>
        </div>
      </div>
      <StrategySavedModal
        open={isSavedModalOpen}
        onClose={() => setIsSavedModalOpen(false)}
        strategyName={formData.strategyName}
        isTimeBased={formData.entryExitMode === 'time_based'}
        onCreateNew={() => {
          resetForm();
          navigate('/algo/create-strategy');
        }}
        onNavigateToSignals={() => navigate('/algo/signals')}
      />
      <Modal
        title="Strategy Description"
        open={isDescriptionModalOpen}
        onOk={() => setIsDescriptionModalOpen(false)}
        onCancel={() => setIsDescriptionModalOpen(false)}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ marginTop: 16, marginBottom: 30 }}>
          <Input.TextArea
            placeholder="Enter strategy description..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={6}
            maxLength={200}
            showCount
          />
        </div>
      </Modal>
      <SubscriptionCheckModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        type="Algo"
      />
    </div>
  );
};

export default QuiktradeAlgo;
